import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Select, { SingleValue, components } from 'react-select';
import { AxiosResponse } from 'axios';
import { Popup } from 'semantic-ui-react';
import moment, { Moment } from 'moment';

import './stat-modals.scss';
import { ReactComponent as Close } from 'assets/images/close.svg';
import LineChartComponent from 'common/charts/line-chart/line-chart';
import PieChartComponent from 'common/charts/pie-chart/pie-chart';
import Dropdown from 'common/dropdown/dropdown';
import ProgressBar from 'common/charts/progress-bar/progress-bar';
import BarChartComponent from 'common/charts/bar-chart/bar-chart';
import SalaryStatData from './salary-stat.json';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowcircleleft.svg';
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrowcircleright.svg';
import { ReactComponent as Dot1Icon } from 'assets/icons/dot01.svg';
import { ReactComponent as Dot2Icon } from 'assets/icons/dot02.svg';
import { ReactComponent as Dot3Icon } from 'assets/icons/dot03.svg';
import { CircularProgressbarWithChildren, buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { getAge } from 'constants/utils';

interface Props {
  isVisible?: boolean;
  onClose: Function;
  headerText?: string;
  modalType: string;
}

const ModalBackground = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
`;

const MiddleArea = styled.div`
  display: flex;
`;
const DateArea = styled.div`
  display: flex;
  margin-top: 20px;
  align-items: center;
  justify-content: center;

  button {
    display: flex;
  }
`;

const StatModal: React.FunctionComponent<Props> = ({ isVisible = false, onClose, headerText, modalType }) => {
  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [date, setDate] = useState<Moment>(moment()); // 선택한 날짜 (근태통계)
  const [viewType, setViewType] = useState<'weekly' | 'monthly'>('weekly'); // 주별/월별 선택
  const [empStatsData, setEmpStatsData] = useState<number[]>([0, 0, 0]); // 지각률,결근률,조퇴율

  const [employeeStatsChart, setEmployeeStatsChart] = useState<number>(1); // 직원통계 표시형태 인덱스
  const [empStatGenderCount, setEmpStatGenderCount] = useState<number[]>([0, 0]); // 성비 명수 [남,여]
  const [empStatGenderRatio, setEmpStatGenderRatio] = useState<number[]>([0, 0]); // 성비 퍼센트 [남,여]
  const [empStatAgeAverage, setEmpStatAgeAverage] = useState<number>(0); // 나이 평균
  const [empStatAgeRatio, setEmpStatAgeRatio] = useState<number[]>([0, 0, 0, 0]); // 나이분포 명수 [20대,30대,40대,50대~] 명수

  const [empStatPayRatio, setEmpStatPayRatio] = useState<number[]>([0, 0, 0]); // 급여유형 비율 [시급,월급,일급]

  // 해당 데이터 및 항목 인덱스 받아서 비율 리턴
  const getEmpStatRatioPercent = (data: number[] = [0], idx: number) => {
    const sum = data.reduce((sum, cur) => {
      return sum + cur;
    }, 0);

    if (sum === 0) return 0;
    return Math.floor((data[idx] / sum) * 100);
  };

  const fetchStatisticData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/attendanceStatistics`, {
        START_DATE: moment(date)
          .startOf(viewType === 'monthly' ? 'month' : 'isoWeek')
          .format('YYYY-MM-DD'),
        END_DATE: moment(date)
          .endOf(viewType === 'monthly' ? 'month' : 'isoWeek')
          .format('YYYY-MM-DD'),
      });
      console.log('/auth/attendanceStatistics_data', data);

      if (data.resultCode === 0) {
        if (data.resultData.length > 0) {
          const {
            totalWorkCount,
            tardy,
            absence,
            earlyLeave,
            gender_m_sum,
            gender_w_sum,
            age_sum,
            age_20,
            age_30,
            age_40,
            age_50,
            pay_hour,
            pay_month,
            pay_day,
          } = data.resultData.reduce((value: any, cv: any) => {
            const workCountValue = cv.willworkCount;
            const tardyValue = cv.startCount;
            const absenceValue = cv.endCount;
            const earlyLeaveValue = cv.nowork;
            const genderMValue = cv.GENDER === '1' ? 1 : 0;
            const genderWValue = cv.GENDER === '0' ? 1 : 0;
            const age20Value = 0 <= getAge(cv.BIRTHDATE) && getAge(cv.BIRTHDATE) < 30 ? 1 : 0;
            const age30Value = 30 <= getAge(cv.BIRTHDATE) && getAge(cv.BIRTHDATE) < 40 ? 1 : 0;
            const age40Value = 40 <= getAge(cv.BIRTHDATE) && getAge(cv.BIRTHDATE) < 50 ? 1 : 0;
            const age50Value = 50 <= getAge(cv.BIRTHDATE) ? 1 : 0;
            const pay_hour_value = cv.PAY_TYPE === '0' ? 1 : 0;
            const pay_month_value = cv.PAY_TYPE === '2' ? 1 : 0;
            const pay_day_value = cv.PAY_TYPE === '1' ? 1 : 0;
            return {
              totalWorkCount: isNaN(value) ? value.totalWorkCount + workCountValue : value + workCountValue,
              tardy: isNaN(value) ? value.tardy + tardyValue : value + tardyValue,
              absence: isNaN(value) ? value.absence + absenceValue : value + absenceValue,
              earlyLeave: isNaN(value) ? value.earlyLeave + earlyLeaveValue : value + earlyLeaveValue,
              gender_m_sum: isNaN(value) ? value.gender_m_sum + genderMValue : value + genderMValue,
              gender_w_sum: isNaN(value) ? value.gender_w_sum + genderWValue : value + genderWValue,
              age_sum: isNaN(value) ? value.age_sum + getAge(cv.BIRTHDATE) : value + getAge(cv.BIRTHDATE),
              age_20: isNaN(value) ? value.age_20 + age20Value : value + age20Value,
              age_30: isNaN(value) ? value.age_30 + age30Value : value + age30Value,
              age_40: isNaN(value) ? value.age_40 + age40Value : value + age40Value,
              age_50: isNaN(value) ? value.age_50 + age50Value : value + age50Value,
              pay_hour: isNaN(value) ? value.pay_hour + pay_hour_value : value + pay_hour_value,
              pay_month: isNaN(value) ? value.pay_month + pay_month_value : value + pay_month_value,
              pay_day: isNaN(value) ? value.pay_day + pay_day_value : value + pay_day_value,
            };
          }, 0);

          const tardyPercent = totalWorkCount === 0 ? 0 : Math.floor((tardy / totalWorkCount) * 100);
          const absencePercent = totalWorkCount === 0 ? 0 : Math.floor((absence / totalWorkCount) * 100);
          const earlyLeavePercent = totalWorkCount === 0 ? 0 : Math.floor((earlyLeave / totalWorkCount) * 100);
          const genderManPercent = Math.floor((gender_m_sum / data.resultData.length) * 100);
          const genderwomanPercent = Math.floor((gender_w_sum / data.resultData.length) * 100);
          const ageAverage = Math.floor(age_sum / data.resultData.length);

          setEmpStatsData([tardyPercent, absencePercent, earlyLeavePercent]);
          setEmpStatGenderCount([
            data.resultData.filter((i: any) => i.GENDER === '1').length,
            data.resultData.filter((i: any) => i.GENDER === '0').length,
          ]);
          setEmpStatGenderRatio([genderManPercent, genderwomanPercent]);
          setEmpStatAgeAverage(ageAverage);
          setEmpStatAgeRatio([age_20, age_30, age_40, age_50]);
          setEmpStatPayRatio([pay_hour, pay_month, pay_day]);
        }
      } else {
        throw new Error('/auth/attendanceStatistics');
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice /auth/attendanceStatistics');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const onCloseModal = () => {
    onClose(false);
  };

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '30px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const salaryChartDataSubType = [30, 50, 30];

  const scheduledTemplates = [
    { value: 'weekly', label: '주별' },
    { value: 'monthly', label: '월별' },
  ];

  const onViewTypeSelectChange = (newValue: SingleValue<{ value: string; label: string }>) => {
    if (newValue?.value === 'weekly' || newValue?.value === 'monthly') setViewType(newValue.value);
  };

  const paginateAbsenteeismStatsCharts = (side: string) => {
    if (viewType === 'weekly') {
      if (side === 'LEFT') {
        const prevWeekDate = moment(date).subtract(1, 'week');
        setDate(prevWeekDate);
      }
      if (side === 'RIGHT') {
        const nextWeekDate = moment(date).add(1, 'week');
        setDate(nextWeekDate);
      }
    } else {
      if (side === 'LEFT') {
        const prevMonth = moment(date).subtract(1, 'month');
        setDate(prevMonth);
      }
      if (side === 'RIGHT') {
        const nextMonth = moment(date).add(1, 'month');
        setDate(nextMonth);
      }
    }
  };

  const employeeStatsCharts = (side: string) => {
    if (side === 'RIGHT') {
      setEmployeeStatsChart((employeeStatsChart % 3) + 1);
    }
    if (side === 'LEFT') {
      const value = employeeStatsChart !== 1 ? employeeStatsChart - 1 : 3;
      setEmployeeStatsChart(value);
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchStatisticData();
    }
  }, [isVisible, viewType, date]);

  let renderChart;
  if (modalType === 'salaryStats') {
    renderChart = (
      <div className="salary-stats-chart">
        <div className="modal-action">
          <div className="modal-action__close" onClick={onCloseModal}>
            <Close />
          </div>
        </div>
        <div className="salary-stats-chart__modal-header">급여통계</div>
        <div className="salary-stats-chart__modal-subheader">
          다음 급여정산일은 <span className="salary-stats-chart__modal-subheader__date">2023.01.01</span> 입니다
        </div>
        <div className="salary-stats-chart__modal-body">
          <div>
            <div className="salary-stats-chart__chart">
              <BarChartComponent data={SalaryStatData} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (modalType === 'absenteeismStats') {
    renderChart = (
      <div className="absenteeism-stats-chart">
        <div className="modal-action">
          <div className="modal-action__close" onClick={onCloseModal}>
            <Close />
          </div>
        </div>
        <div className="absenteeism-stats-chart__modal-header">근태통계</div>
        <div className="absenteeism-stats-chart__modal">
          <Select
            className="absenteeism-stats-chart__modal-dropdown"
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: () => (
                <div style={{ paddingRight: '15px' }}>
                  <ArrowDownIcon />
                </div>
              ),
            }}
            styles={colourStyles}
            options={scheduledTemplates}
            defaultValue={viewType === 'weekly' ? scheduledTemplates[0] : scheduledTemplates[1]}
            isSearchable={false}
            onChange={onViewTypeSelectChange}
          />
          <DateArea>
            <button
              className="absenteeism-stats-chart__modal-body__left"
              onClick={() => paginateAbsenteeismStatsCharts('LEFT')}>
              <LeftArrowIcon />
            </button>
            <span className="absenteeism-stats-chart__date">
              {viewType === 'weekly' ? (
                <>
                  {moment(date).startOf('isoWeek').format('YYYY.MM.DD')} ~{' '}
                  {moment(date).endOf('isoWeek').format('YYYY.MM.DD')}
                </>
              ) : (
                <>{moment(date).format('YYYY.MM')}</>
              )}
            </span>
            <button
              className="absenteeism-stats-chart__modal-body__right"
              onClick={() => paginateAbsenteeismStatsCharts('RIGHT')}>
              <RightArrowIcon />
            </button>
          </DateArea>
        </div>
        <div className="absenteeism-stats-chart__modal-body">
          <MiddleArea>
            <div className="absenteeism-stats-chart__modal-body__middle">
              <div className="absenteeism-stats-chart__chart">
                <CircularProgressbarWithChildren
                  value={empStatsData[0]}
                  strokeWidth={15}
                  styles={buildStyles({
                    pathColor: '#da7772',
                    trailColor: '#f7d8d6',
                  })}>
                  <div className="absenteeism-stats-chart__chart__chart1">
                    {empStatsData[0]}
                    <span className="absenteeism-stats-chart__chart__perc1">%</span>
                  </div>
                </CircularProgressbarWithChildren>
              </div>
              <div>
                <div className="absenteeism-stats-chart__modal-footer absenteeism-stats-chart__color1">지각률</div>
              </div>
            </div>
            <div className="absenteeism-stats-chart__modal-body__middle">
              <div className="absenteeism-stats-chart__chart">
                <CircularProgressbarWithChildren
                  value={empStatsData[1]}
                  strokeWidth={15}
                  styles={buildStyles({
                    pathColor: '#fed159',
                    trailColor: '#fff1cd',
                  })}>
                  <div className="absenteeism-stats-chart__chart__chart2">
                    {empStatsData[1]}
                    <span className="absenteeism-stats-chart__chart__perc2">%</span>
                  </div>
                </CircularProgressbarWithChildren>
              </div>
              <div>
                <div className="absenteeism-stats-chart__modal-footer absenteeism-stats-chart__color2">결근률</div>
              </div>
            </div>
            <div className="absenteeism-stats-chart__modal-body__middle">
              <div className="absenteeism-stats-chart__chart">
                <CircularProgressbarWithChildren
                  value={empStatsData[2]}
                  strokeWidth={15}
                  styles={buildStyles({
                    pathColor: '#fd966c',
                    trailColor: '#fedfd3',
                  })}>
                  <div className="absenteeism-stats-chart__chart__chart3">
                    {empStatsData[2]}
                    <span className="absenteeism-stats-chart__chart__perc3">%</span>
                  </div>
                </CircularProgressbarWithChildren>
              </div>
              <div>
                <div className="absenteeism-stats-chart__modal-footer absenteeism-stats-chart__color3">조퇴율</div>
              </div>
            </div>
          </MiddleArea>
        </div>
      </div>
    );
  }

  if (modalType === 'employeeStats') {
    renderChart = (
      <div className="employee-stats-chart">
        <div className="modal-action">
          <div className="modal-action__close" onClick={onCloseModal}>
            <Close />
          </div>
        </div>
        <div className="employee-stats-chart__modal-header">직원통계</div>
        {employeeStatsChart === 1 && <div className="employee-stats-chart__modal-subheader">전체 구성원 성비</div>}
        {employeeStatsChart === 2 && (
          <div className="employee-stats-chart__modal-subheader">
            평균 나이 분포 <span className="employee-stats-chart__modal-subheader__span"> {empStatAgeAverage}</span>
            <span className="employee-stats-chart__modal-subheader__span2"> 세</span>
          </div>
        )}
        {employeeStatsChart === 3 && <div className="employee-stats-chart__modal-subheader">게약 유형별 비율</div>}
        <div className="employee-stats-chart__modal-body">
          <div
            className={
              employeeStatsChart === 2
                ? 'employee-stats-chart__modal-body__left-up'
                : 'employee-stats-chart__modal-body__left'
            }
            onClick={() => employeeStatsCharts('LEFT')}>
            <LeftArrowIcon />
          </div>
          <div className="employee-stats-chart__modal-body__middle">
            {employeeStatsChart === 1 && (
              <div>
                <div className="employee-stats-chart__pie-chart">
                  <CircularProgressbarWithChildren
                    value={getEmpStatRatioPercent(empStatGenderRatio, 0)}
                    strokeWidth={20}
                    styles={buildStyles({
                      pathColor: '#4aae9e',
                      trailColor: '#fed159',
                      strokeLinecap: 'butt',
                    })}></CircularProgressbarWithChildren>
                </div>
              </div>
            )}
            {employeeStatsChart === 2 && (
              <div>
                <div className="employee-stats-chart__progress-bar">
                  <div className="employee-stats-chart__progress-bar__line">
                    <div className="employee-stats-chart__progress-bar__text">~20대</div>
                    <Popup
                      className="employee-stats-chart__popup"
                      flowing={true}
                      trigger={
                        <span style={{ width: '100%' }}>
                          <ProgressBar completed={getEmpStatRatioPercent(empStatAgeRatio, 0)} />
                        </span>
                      }
                      position="top center"
                      inverted
                      offset={[getEmpStatRatioPercent(empStatAgeRatio, 0), 0]}>
                      <div className="employee-stats-chart__popup">
                        <span className="employee-stats-chart__popup__span">{empStatAgeRatio[0]}</span>명
                      </div>
                    </Popup>
                  </div>
                  <div className="employee-stats-chart__progress-bar__line">
                    <div className="employee-stats-chart__progress-bar__text">30대</div>
                    <Popup
                      className="employee-stats-chart__popup"
                      flowing={true}
                      trigger={
                        <span style={{ width: '100%' }}>
                          <ProgressBar completed={getEmpStatRatioPercent(empStatAgeRatio, 1)} />
                        </span>
                      }
                      position="top center"
                      inverted
                      offset={[getEmpStatRatioPercent(empStatAgeRatio, 1), 0]}>
                      <div className="employee-stats-chart__popup">
                        <span className="employee-stats-chart__popup__span">{empStatAgeRatio[1]}</span>명
                      </div>
                    </Popup>
                  </div>
                  <div className="employee-stats-chart__progress-bar__line">
                    <div className="employee-stats-chart__progress-bar__text">40대</div>
                    <Popup
                      className="employee-stats-chart__popup"
                      flowing={true}
                      trigger={
                        <span style={{ width: '100%' }}>
                          <ProgressBar completed={getEmpStatRatioPercent(empStatAgeRatio, 2)} />
                        </span>
                      }
                      position="top center"
                      inverted
                      offset={[getEmpStatRatioPercent(empStatAgeRatio, 2), 0]}>
                      <div className="employee-stats-chart__popup">
                        <span className="employee-stats-chart__popup__span">{empStatAgeRatio[2]}</span>명
                      </div>
                    </Popup>
                  </div>
                  <div className="employee-stats-chart__progress-bar__line">
                    <div className="employee-stats-chart__progress-bar__text">50대~</div>
                    <Popup
                      className="employee-stats-chart__popup"
                      flowing={true}
                      trigger={
                        <span style={{ width: '100%' }}>
                          <ProgressBar completed={getEmpStatRatioPercent(empStatAgeRatio, 3)} />
                        </span>
                      }
                      position="top center"
                      inverted
                      offset={[getEmpStatRatioPercent(empStatAgeRatio, 3), 0]}>
                      <div className="employee-stats-chart__popup">
                        <span className="employee-stats-chart__popup__span">{empStatAgeRatio[3]}</span>명
                      </div>
                    </Popup>
                  </div>
                </div>
              </div>
            )}
            {employeeStatsChart === 3 && (
              <div>
                <div className="employee-stats-chart__pie-chart">
                  <CircularProgressbarWithChildren
                    value={getEmpStatRatioPercent(empStatPayRatio, 0) + getEmpStatRatioPercent(empStatPayRatio, 1)}
                    strokeWidth={20}
                    styles={buildStyles({
                      pathColor: '#fed159',
                      trailColor: '#fd966c',
                      strokeLinecap: 'butt',
                    })}>
                    <CircularProgressbar
                      value={getEmpStatRatioPercent(empStatPayRatio, 0)}
                      strokeWidth={20}
                      styles={buildStyles({
                        pathColor: '#4aae9e',
                        trailColor: 'transparent',
                        strokeLinecap: 'butt',
                      })}
                    />
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            )}
          </div>
          <div
            className={
              employeeStatsChart === 2
                ? 'employee-stats-chart__modal-body__right-up'
                : 'employee-stats-chart__modal-body__right'
            }
            onClick={() => employeeStatsCharts('RIGHT')}>
            <RightArrowIcon />
          </div>
        </div>
        <div className="employee-stats-chart__modal-footer">
          {employeeStatsChart === 1 && (
            <div className="employee-stats-chart__modal-footer__legend">
              <table className="employee-stats-chart__modal-footer__table">
                <tr>
                  <td className="employee-stats-chart__modal-footer__td">
                    <div className="employee-stats-chart__modal-footer__items">
                      <Dot1Icon />
                      <span className="employee-stats-chart__modal-footer__text"> 남성</span>
                      <span className="employee-stats-chart__modal-footer__span">
                        {' '}
                        {empStatGenderCount[0]}명 ({getEmpStatRatioPercent(empStatGenderRatio, 0)}%)
                      </span>
                    </div>
                  </td>
                  <td className="employee-stats-chart__modal-footer__td">
                    <div className="employee-stats-chart__modal-footer__items">
                      <Dot2Icon />
                      <span className="employee-stats-chart__modal-footer__text">여성</span>
                      <span className="employee-stats-chart__modal-footer__span">
                        {' '}
                        {empStatGenderCount[1]}명 ({getEmpStatRatioPercent(empStatGenderRatio, 1)}%)
                      </span>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          )}
          {employeeStatsChart === 3 && (
            <div className="employee-stats-chart__modal-footer__legend">
              <table className="employee-stats-chart__modal-footer__table">
                <tr>
                  <td className="employee-stats-chart__modal-footer__td">
                    <div className="employee-stats-chart__modal-footer__items">
                      <Dot1Icon />
                      <span className="employee-stats-chart__modal-footer__text"> 시급</span>
                      <span className="employee-stats-chart__modal-footer__span">
                        {empStatPayRatio[0]}명 ({getEmpStatRatioPercent(empStatPayRatio, 0)}%)
                      </span>
                    </div>
                  </td>
                  <td className="employee-stats-chart__modal-footer__td">
                    <div className="employee-stats-chart__modal-footer__items">
                      <Dot2Icon />
                      <span className="employee-stats-chart__modal-footer__text">월급</span>
                      <span className="employee-stats-chart__modal-footer__span">
                        {empStatPayRatio[1]}명 ({getEmpStatRatioPercent(empStatPayRatio, 1)}%)
                      </span>
                    </div>
                  </td>
                  <td className="employee-stats-chart__modal-footer__td">
                    <div className="employee-stats-chart__modal-footer__items">
                      <Dot3Icon />
                      <span className="employee-stats-chart__modal-footer__text">일급</span>
                      <span className="employee-stats-chart__modal-footer__span">
                        {empStatPayRatio[2]}명 ({getEmpStatRatioPercent(empStatPayRatio, 2)}%)
                      </span>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isVisible === false) return null;
  return (
    <div className="modal">
      {renderChart}
      <ModalBackground onClick={onCloseModal} />
    </div>
  );
};
export default StatModal;
